import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import BillInput from './components/BillInput';
import UpgradeSelector from './components/UpgradeSelector';
import ResultsComparison from './components/ResultsComparison';
import RebateBreakdown from './components/RebateBreakdown';
import LongTermProjection from './components/LongTermProjection';
import WhyActNow from './components/WhyActNow';

const FL_RATE_PER_KWH = 0.157;
const ANNUAL_RATE_INCREASE = 0.035;
const SOLAR_COST_PER_WATT = 2.5;
const NEW_AC_COST = 8500;
const INSULATION_COST_PER_SQFT = 1.5;
const NEW_AC_SEER = 20;
const SOLAR_KWH_PER_KW_YEAR_FL = 1500;

export default function SavingsCalculatorPage() {
  const [monthlyBill, setMonthlyBill] = useState(250);
  const [homeSize, setHomeSize] = useState(1800);
  const [currentSeer, setCurrentSeer] = useState(10);
  const [solarOnly, setSolarOnly] = useState(true);
  const [solarPlusAC, setSolarPlusAC] = useState(true);
  const [solarACInsulation, setSolarACInsulation] = useState(true);
  const [solarKw, setSolarKw] = useState(8);
  const [showForm, setShowForm] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formSubmitting, setFormSubmitting] = useState(false);

  const monthlyKwh = monthlyBill / FL_RATE_PER_KWH;
  const coolingPercent = 0.55;
  const coolingKwh = monthlyKwh * coolingPercent;

  const solarCost = Math.round(solarKw * 1000 * SOLAR_COST_PER_WATT);
  const acCost = NEW_AC_COST;
  const insulationCost = Math.round(homeSize * INSULATION_COST_PER_SQFT);

  const results = useMemo(() => {
    const arr: {
      label: string;
      icon: string;
      monthlySavings: number;
      annualSavings: number;
      fiveYearSavings: number;
      tenYearSavings: number;
      systemCost: number;
      totalIncentives: number;
      netCost: number;
      roiYears: number;
      savingsPercent: number;
      color: string;
      badge: string;
    }[] = [];

    const solarMonthlyKwhProduced = (solarKw * SOLAR_KWH_PER_KW_YEAR_FL) / 12;
    const solarMonthlySavings = Math.min(
      solarMonthlyKwhProduced * FL_RATE_PER_KWH,
      monthlyBill * 0.95
    );

    const seerSavingsRatio = Math.max(0, 1 - currentSeer / NEW_AC_SEER);
    const acMonthlySavings = coolingKwh * seerSavingsRatio * FL_RATE_PER_KWH;

    const insulationSavingsPercent = 0.2;
    const insulationMonthlySavings =
      coolingKwh * insulationSavingsPercent * FL_RATE_PER_KWH;

    const getCumulativeSavings = (annualSav: number, years: number) => {
      let total = 0;
      const annualBill = monthlyBill * 12;
      for (let y = 1; y <= years; y++) {
        const inflatedBill = annualBill * Math.pow(1 + ANNUAL_RATE_INCREASE, y);
        const savingsRatio = annualSav / annualBill;
        total += Math.round(inflatedBill * savingsRatio);
      }
      return total;
    };

    if (solarOnly) {
      const monthly = Math.round(solarMonthlySavings);
      const annual = monthly * 12;
      const solarITC = Math.round(solarCost * 0.3);
      const salesTaxSaved = Math.round(solarCost * 0.06);
      const totalIncentives = solarITC + salesTaxSaved;
      const netCost = solarCost - totalIncentives;
      const fiveYear = getCumulativeSavings(annual, 5);
      const tenYear = getCumulativeSavings(annual, 10);
      const roi = netCost / Math.max(annual, 1);
      const pct = Math.min((monthly / monthlyBill) * 100, 100);

      arr.push({
        label: 'Solar Only',
        icon: 'SOLAR',
        monthlySavings: monthly,
        annualSavings: annual,
        fiveYearSavings: fiveYear,
        tenYearSavings: tenYear,
        systemCost: solarCost,
        totalIncentives,
        netCost,
        roiYears: roi,
        savingsPercent: pct,
        color: '#E8A317',
        badge: 'SOLAR',
      });
    }

    if (solarPlusAC) {
      const combinedMonthly = Math.min(
        Math.round(solarMonthlySavings + acMonthlySavings),
        Math.round(monthlyBill * 0.95)
      );
      const annual = combinedMonthly * 12;
      const totalSystemCost = solarCost + acCost;
      const solarITC = Math.round(solarCost * 0.3);
      const salesTaxSaved = Math.round(solarCost * 0.06);
      const acTaxCredit = 2000;
      const utilityRebateAC = 750;
      const totalIncentives =
        solarITC + salesTaxSaved + acTaxCredit + utilityRebateAC;
      const netCost = totalSystemCost - totalIncentives;
      const fiveYear = getCumulativeSavings(annual, 5);
      const tenYear = getCumulativeSavings(annual, 10);
      const roi = netCost / Math.max(annual, 1);
      const pct = Math.min((combinedMonthly / monthlyBill) * 100, 100);

      arr.push({
        label: 'Solar + New AC',
        icon: 'SOLAR + AC',
        monthlySavings: combinedMonthly,
        annualSavings: annual,
        fiveYearSavings: fiveYear,
        tenYearSavings: tenYear,
        systemCost: totalSystemCost,
        totalIncentives,
        netCost,
        roiYears: roi,
        savingsPercent: pct,
        color: '#C41E3A',
        badge: 'POPULAR',
      });
    }

    if (solarACInsulation) {
      const rawMonthly =
        solarMonthlySavings + acMonthlySavings + insulationMonthlySavings;
      const combinedMonthly = Math.min(
        Math.round(rawMonthly),
        Math.round(monthlyBill * 0.97)
      );
      const annual = combinedMonthly * 12;
      const totalSystemCost = solarCost + acCost + insulationCost;
      const solarITC = Math.round(solarCost * 0.3);
      const salesTaxSaved = Math.round(solarCost * 0.06);
      const acTaxCredit = 2000;
      const utilityRebateAC = 750;
      const insulationTaxCredit = Math.min(
        Math.round(insulationCost * 0.3),
        1200
      );
      const utilityRebateIns = 600;
      const totalIncentives =
        solarITC +
        salesTaxSaved +
        acTaxCredit +
        utilityRebateAC +
        insulationTaxCredit +
        utilityRebateIns;
      const netCost = totalSystemCost - totalIncentives;
      const fiveYear = getCumulativeSavings(annual, 5);
      const tenYear = getCumulativeSavings(annual, 10);
      const roi = netCost / Math.max(annual, 1);
      const pct = Math.min((combinedMonthly / monthlyBill) * 100, 100);

      arr.push({
        label: 'Solar + AC + Insulation',
        icon: 'COMPLETE',
        monthlySavings: combinedMonthly,
        annualSavings: annual,
        fiveYearSavings: fiveYear,
        tenYearSavings: tenYear,
        systemCost: totalSystemCost,
        totalIncentives,
        netCost,
        roiYears: roi,
        savingsPercent: pct,
        color: '#1B3A6B',
        badge: 'BEST VALUE',
      });
    }

    return arr;
  }, [
    monthlyBill,
    solarKw,
    currentSeer,
    homeSize,
    solarOnly,
    solarPlusAC,
    solarACInsulation,
    solarCost,
    acCost,
    insulationCost,
    coolingKwh,
  ]);

  const projections = results.map((r) => ({
    label: r.label,
    color: r.color,
    annualSavings: r.annualSavings,
    netCost: r.netCost,
  }));

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const form = e.currentTarget;
    const formData = new FormData(form);

    const textarea = form.querySelector('textarea');
    if (textarea && textarea.value.length > 500) {
      // Simple guard – the textarea already has maxLength, but we keep this check.
      return;
    }

    setFormSubmitting(true);
    try {
      await fetch('https://readdy.ai/api/form/d6ch00mqd308q1a09irg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(
          Object.fromEntries(formData.entries() as Iterable<[string, string]>)
        ).toString(),
      });
      setFormSubmitted(true);
    } catch (error) {
      console.error('Form submission error:', error);
      setFormSubmitted(true);
    } finally {
      setFormSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="pt-28 pb-12 relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/f54b906c6d60b5034fd29c9c2b78f9b8.jpg"
            alt="Florida home with solar panels"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-white"></div>
        </div>
        <div className="max-w-[1400px] mx-auto px-6 relative z-10">
          <div className="text-center">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 font-medium text-sm"
            >
              <i className="ri-arrow-left-line"></i>
              Back to Home
            </Link>
            <h1 className="text-4xl lg:text-5xl font-bold text-white mb-4">
              How Much Can You{' '}
              <span className="text-brand-red">Really</span> Save?
            </h1>
            <p className="text-lg text-white/80 max-w-2xl mx-auto">
              Enter your current utility bill and see exactly how much each
              upgrade saves you — with real Florida energy data, tax credits,
              and rebates calculated instantly.
            </p>
          </div>
        </div>
      </section>

      {/* Calculator Section */}
      <section className="py-12 bg-gradient-to-b from-white to-brand-cream">
        <div className="max-w-[1400px] mx-auto px-6">
          {/* Step 1: Bill Input */}
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-brand-red text-white text-sm font-bold">
                1
              </div>
              <h2 className="text-2xl font-bold text-gray-900">
                Tell Us About Your Current Energy Usage
              </h2>
            </div>
            <div className="max-w-3xl">
              <BillInput
                monthlyBill={monthlyBill}
                setMonthlyBill={setMonthlyBill}
                homeSize={homeSize}
                setHomeSize={setHomeSize}
                currentSeer={currentSeer}
                setCurrentSeer={setCurrentSeer}
              />
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 max-w-3xl">
              <div className="bg-white rounded-xl p-4 border border-gray-200 text-center">
                <div className="text-xs text-gray-500 mb-1">
                  Monthly kWh Usage
                </div>
                <div className="text-lg font-bold text-gray-900">
                  {Math.round(monthlyKwh).toLocaleString()}
                </div>
              </div>
              <div className="bg-white rounded-xl p-4 border border-gray-200 text-center">
                <div className="text-xs text-gray-500 mb-1">
                  Annual Electric Cost
                </div>
                <div className="text-lg font-bold text-red-500">
                  ${(monthlyBill * 12).toLocaleString()}
                </div>
              </div>
              <div className="bg-white rounded-xl p-4 border border-gray-200 text-center">
                <div className="text-xs text-gray-500 mb-1">
                  Cooling Cost (~55%)
                </div>
                <div className="text-lg font-bold text-gray-900">
                  ${Math.round(monthlyBill * 0.55)}/mo
                </div>
              </div>
              <div className="bg-white rounded-xl p-4 border border-gray-200 text-center">
                <div className="text-xs text-gray-500 mb-1">
                  10-Yr Cost (No Action)
                </div>
                <div className="text-lg font-bold text-red-500">
                  $
                  {Math.round(
                    monthlyBill *
                      12 *
                      ((Math.pow(1.035, 10) - 1) / 0.035 + Math.pow(1.035, 10))
                  ).toLocaleString()}
                </div>
              </div>
            </div>
          </div>

          {/* Step 2: Upgrade Selection */}
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-brand-red text-white text-sm font-bold">
                2
              </div>
              <h2 className="text-2xl font-bold text-gray-900">
                Choose Your Upgrade Packages to Compare
              </h2>
            </div>
            <div className="max-w-3xl">
              <UpgradeSelector
                solarOnly={solarOnly}
                setSolarOnly={setSolarOnly}
                solarPlusAC={solarPlusAC}
                setSolarPlusAC={setSolarPlusAC}
                solarACInsulation={solarACInsulation}
                setSolarACInsulation={setSolarACInsulation}
                solarKw={solarKw}
                setSolarKw={setSolarKw}
              />
            </div>
          </div>

          {/* Step 3: Results */}
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-brand-red text-white text-sm font-bold">
                3
              </div>
              <h2 className="text-2xl font-bold text-gray-900">
                Your Personalized Savings Breakdown
              </h2>
            </div>
            <ResultsComparison results={results} monthlyBill={monthlyBill} />
          </div>

          {/* Step 4: Rebates & Tax Credits */}
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-brand-red text-white text-sm font-bold">
                4
              </div>
              <h2 className="text-2xl font-bold text-gray-900">
                Tax Credits &amp; Rebates
              </h2>
            </div>
            <div className="max-w-3xl">
              <RebateBreakdown
                solarOnly={solarOnly}
                solarPlusAC={solarPlusAC}
                solarACInsulation={solarACInsulation}
                solarCost={solarCost}
                acCost={acCost}
                insulationCost={insulationCost}
              />
            </div>
          </div>

          {/* Step 5: Long-Term Projection */}
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-brand-red text-white text-sm font-bold">
                5
              </div>
              <h2 className="text-2xl font-bold text-gray-900">
                5-Year &amp; 10-Year Projections
              </h2>
            </div>
            <LongTermProjection projections={projections} monthlyBill={monthlyBill} />
          </div>
        </div>
      </section>

      {/* Why Act Now */}
      <section className="py-16 bg-white">
        <div className="max-w-[1400px] mx-auto px-6">
          <WhyActNow />
        </div>
      </section>

      {/* CTA / Lead Form */}
      <section className="py-16 bg-gradient-to-b from-brand-cream to-white">
        <div className="max-w-[800px] mx-auto px-6">
          <div className="text-center mb-8">
            <div className="inline-block bg-brand-red-light text-brand-red px-4 py-2 rounded-full text-xs font-bold mb-3">
              FREE &bull; NO OBLIGATION
            </div>
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-3">
              Get Your Free Home Assessment
            </h2>
            <p className="text-sm text-gray-600 max-w-xl mx-auto">
              Our energy experts will visit your home, analyze your roof, inspect
              your AC, and give you an exact quote — completely free.
            </p>
          </div>

          {!showForm ? (
            <div className="text-center space-y-4">
              <button
                onClick={() => setShowForm(true)}
                className="inline-flex items-center gap-3 bg-brand-red text-white px-10 py-5 rounded-full text-lg font-bold hover:bg-brand-red-dark transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-calendar-check-line"></i>
                Schedule My Free Assessment
              </button>
              <div className="flex items-center justify-center gap-6 text-sm text-gray-500">
                <span className="flex items-center gap-1">
                  <i className="ri-check-line text-green-500"></i> No cost
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-check-line text-green-500"></i> No pressure
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-check-line text-green-500"></i> 30 min visit
                </span>
              </div>
              <p className="text-sm text-gray-500 mt-4">
                Or call us directly:
                <a
                  href="tel:+18005551234"
                  className="text-brand-red font-bold ml-1 hover:underline cursor-pointer"
                >
                  (800) 555-1234
                </a>
              </p>
            </div>
          ) : formSubmitted ? (
            <div className="bg-green-50 rounded-2xl p-8 text-center border border-green-200">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-green-100 mx-auto mb-4">
                <i className="ri-check-double-line text-green-600 text-3xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Thank You!
              </h3>
              <p className="text-sm text-gray-600">
                We&apos;ve received your request. One of our energy experts will
                contact you within 24 hours to schedule your free home
                assessment.
              </p>
            </div>
          ) : (
            <form
              id="assessment-form"
              data-readdy-form
              onSubmit={handleFormSubmit}
              className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100 space-y-5"
            >
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="full_name"
                    required
                    placeholder="John Smith"
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-red/30 focus:border-brand-red"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="john@example.com"
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-red/30 focus:border-brand-red"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Phone *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    required
                    placeholder="(555) 123-4567"
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-red/30 focus:border-brand-red"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    ZIP Code *
                  </label>
                  <input
                    type="text"
                    name="zip_code"
                    required
                    placeholder="33101"
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-red/30 focus:border-brand-red"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Current Monthly Electric Bill
                </label>
                <input
                  type="text"
                  name="monthly_bill"
                  value={`$${monthlyBill}`}
                  readOnly
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm bg-gray-50 text-gray-600"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Interested In
                </label>
                <select
                  name="interest"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-red/30 focus:border-brand-red cursor-pointer"
                >
                  <option value="Solar Only">Solar Only</option>
                  <option value="Solar + New AC" selected>
                    Solar + New AC
                  </option>
                  <option value="Solar + AC + Insulation">
                    Solar + AC + Insulation (Best Value)
                  </option>
                  <option value="Not Sure - Need Consultation">
                    Not Sure - Need Consultation
                  </option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Additional Notes (optional)
                </label>
                <textarea
                  name="notes"
                  maxLength={500}
                  rows={3}
                  placeholder="Any details about your home or questions..."
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-red/30 focus:border-brand-red resize-none"
                ></textarea>
              </div>

              <button
                type="submit"
                disabled={formSubmitting}
                className="w-full bg-brand-red text-white py-4 rounded-full text-base font-bold hover:bg-brand-red-dark transition-all duration-300 hover:scale-[1.02] whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {formSubmitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    Submitting...
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line"></i>
                    Request My Free Assessment
                  </>
                )}
              </button>

              <p className="text-[10px] text-gray-400 text-center">
                By submitting, you agree to be contacted about your home energy
                assessment. We respect your privacy and will never share your
                information.
              </p>
            </form>
          )}
        </div>
      </section>

      {/* Trust Badges */}
      <section className="py-12 bg-white">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="w-14 h-14 flex items-center justify-center rounded-full bg-green-100 mx-auto mb-3">
                <i className="ri-shield-check-line text-green-600 text-2xl"></i>
              </div>
              <div className="text-sm font-bold text-gray-900">
                Licensed &amp; Insured
              </div>
              <div className="text-xs text-gray-500">Florida CVC57207</div>
            </div>
            <div className="text-center">
              <div className="w-14 h-14 flex items-center justify-center rounded-full bg-brand-gold-light mx-auto mb-3">
                <i className="ri-star-fill text-brand-gold text-2xl"></i>
              </div>
              <div className="text-sm font-bold text-gray-900">
                4.9 Star Rating
              </div>
              <div className="text-xs text-gray-500">500+ Google Reviews</div>
            </div>
            <div className="text-center">
              <div className="w-14 h-14 flex items-center justify-center rounded-full bg-brand-navy-light mx-auto mb-3">
                <i className="ri-home-smile-line text-brand-navy text-2xl"></i>
              </div>
              <div className="text-sm font-bold text-gray-900">
                10,000+ Homes
              </div>
              <div className="text-xs text-gray-500">Served Across Florida</div>
            </div>
            <div className="text-center">
              <div className="w-14 h-14 flex items-center justify-center rounded-full bg-brand-red-light mx-auto mb-3">
                <i className="ri-hand-heart-line text-brand-red text-2xl"></i>
              </div>
              <div className="text-sm font-bold text-gray-900">
                $0 Down Options
              </div>
              <div className="text-xs text-gray-500">Flexible Financing</div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
